import React, { useState } from 'react';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { DOTTUYENSINH_API } from '@app/utils/apiUrl';
import { Post } from '@app/utils/httpProvider';
import { 
    messageError, 
    messageToast, 
    status200,
    objectLoaiTuyenSinh,
    objectTrangThaiDotTuyenSinh,
    doiTuongValue,
    objectDoiTuong 
} from '../../../../../utils/define';
import { URI_INTERNAL } from '../../../../../utils/pathLocations';
import { Button } from '@components';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';

const schema = yup.object().shape({
    tenDot: yup.string().required(messageError.emptyMessage).max(255, 'Tên đợt không được vượt quá 255 ký tự'),
    namTuyenSinh: yup.number().required(messageError.emptyMessage).min(1, 'Năm tuyển sinh phải lớn hơn 0'),
    loaiTuyenSinh: yup.number().required(messageError.emptyMessage),
    trangThaiDotTuyenSinh: yup.number().required(messageError.emptyMessage),
    doiTuong: yup.number().required(messageError.emptyMessage),
    lePhi: yup.number().min(0, 'Lệ phí không được nhỏ hơn 0').nullable(),
    chiTieuQuan: yup.number().min(0, 'Chỉ tiêu quân không được nhỏ hơn 0').nullable(),
    chiTieuDan: yup.number().min(0, 'Chỉ tiêu dân không được nhỏ hơn 0').nullable(),
    ngayNhanHoSo: yup.date().nullable(),
    ngayKetThucNhanHoSo: yup.date()
        .nullable()
        .min(yup.ref('ngayNhanHoSo'), 'Ngày kết thúc phải lớn hơn hoặc bằng ngày bắt đầu'),
});

const DotTuyenSinhAdd = () => {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);

    const addFunc = useMutation((value) =>
        Post(DOTTUYENSINH_API.ADD_ITEM, null, value, false)
    );

    const handleCreate = async (values) => {
        const doiTuong = parseInt(values.doiTuong);
        const model = {
            ...values,
            chiTieuQuan: (doiTuong === doiTuongValue.quan || doiTuong === doiTuongValue.quanVaDan) ? (values.chiTieuQuan === '' ? 0 : values.chiTieuQuan) : 0,
            chiTieuDan: (doiTuong === doiTuongValue.khac || doiTuong === doiTuongValue.quanVaDan) ? (values.chiTieuDan === '' ? 0 : values.chiTieuDan) : 0,
            lePhi: values.lePhi === '' ? 0 : values.lePhi,
            ngayNhanHoSo: values.ngayNhanHoSo || null,
            ngayKetThucNhanHoSo: values.ngayKetThucNhanHoSo || null,
        };

        addFunc.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastCreateError);
                } else {
                    setIsLoading(true);
                    toast.success(messageToast.messageToastCreateSuccess);
                    history.push(URI_INTERNAL.DOTTUYENSINH_PATH);
                }
            },
            onError: (res) => {
                toast.error(
                    res.message || 'Đã có lỗi xảy ra, vui lòng thử lại !'
                );
            }
        });
    };

    return (
        <Card>
            <Card.Header as="h5">Thêm mới đợt tuyển sinh</Card.Header>
            <Card.Body>
                <Formik
                    validationSchema={schema}
                    onSubmit={(values) => handleCreate(values)}
                    initialValues={{
                        tenDot: '',
                        namTuyenSinh: new Date().getFullYear(),
                        loaiTuyenSinh: 0,
                        trangThaiDotTuyenSinh: 0,
                        doiTuong: 1,
                        chiTieuQuan: '',
                        chiTieuDan: '',
                        lePhi: 0,
                        ngayNhanHoSo: '',
                        ngayKetThucNhanHoSo: ''
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Tên đợt',
                                        name: 'tenDot',
                                        value: values.tenDot,
                                        onChange: handleChange,
                                        isInvalid: errors.tenDot,
                                        error: errors.tenDot,
                                        required: true
                                    },
                                    {
                                        label: 'Năm tuyển sinh',
                                        name: 'namTuyenSinh',
                                        type: 'number',
                                        value: values.namTuyenSinh,
                                        onChange: handleChange,
                                        isInvalid: errors.namTuyenSinh,
                                        error: errors.namTuyenSinh,
                                        required: true
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Loại tuyển sinh',
                                        name: 'loaiTuyenSinh',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="loaiTuyenSinh"
                                                value={values.loaiTuyenSinh}
                                                onChange={handleChange}
                                                isInvalid={errors.loaiTuyenSinh}
                                            >
                                                {objectLoaiTuyenSinh.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.loaiTuyenSinh
                                    },
                                    {
                                        label: 'Đối tượng',
                                        name: 'doiTuong',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="doiTuong"
                                                value={values.doiTuong}
                                                onChange={handleChange}
                                                isInvalid={errors.doiTuong}
                                            >
                                                {objectDoiTuong.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.doiTuong
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    (parseInt(values.doiTuong) === doiTuongValue.quan || parseInt(values.doiTuong) === doiTuongValue.quanVaDan) ? {
                                        label: 'Chỉ tiêu quân',
                                        name: 'chiTieuQuan',
                                        type: 'number',
                                        value: values.chiTieuQuan,
                                        onChange: handleChange,
                                        isInvalid: errors.chiTieuQuan,
                                        error: errors.chiTieuQuan
                                    } : null,
                                    (parseInt(values.doiTuong) === doiTuongValue.khac || parseInt(values.doiTuong) === doiTuongValue.quanVaDan) ? {
                                        label: 'Chỉ tiêu dân',
                                        name: 'chiTieuDan',
                                        type: 'number',
                                        value: values.chiTieuDan,
                                        onChange: handleChange,
                                        isInvalid: errors.chiTieuDan,
                                        error: errors.chiTieuDan
                                    } : null,
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Lệ phí',
                                        name: 'lePhi',
                                        type: 'number',
                                        value: values.lePhi,
                                        onChange: handleChange,
                                        isInvalid: errors.lePhi,
                                        error: errors.lePhi
                                    },
                                    {
                                        label: 'Trạng thái',
                                        name: 'trangThaiDotTuyenSinh',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="trangThaiDotTuyenSinh"
                                                value={values.trangThaiDotTuyenSinh}
                                                onChange={handleChange}
                                                isInvalid={errors.trangThaiDotTuyenSinh}
                                            >
                                                {objectTrangThaiDotTuyenSinh.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.trangThaiDotTuyenSinh
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Ngày nhận hồ sơ',
                                        name: 'ngayNhanHoSo',
                                        type: 'date',
                                        value: values.ngayNhanHoSo,
                                        onChange: handleChange,
                                        isInvalid: errors.ngayNhanHoSo,
                                        error: errors.ngayNhanHoSo
                                    },
                                    {
                                        label: 'Ngày kết thúc nhận',
                                        name: 'ngayKetThucNhanHoSo',
                                        type: 'date',
                                        value: values.ngayKetThucNhanHoSo,
                                        onChange: handleChange,
                                        isInvalid: errors.ngayKetThucNhanHoSo,
                                        error: errors.ngayKetThucNhanHoSo
                                    },
                                ]}
                            />
                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary" disabled={isLoading}>
                                            Thêm
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.DOTTUYENSINH_PATH)} theme="secondary">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
                {isLoading && <div>Đang lưu...</div>}
            </Card.Body>
        </Card>
    );
};

export default DotTuyenSinhAdd;
